import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { BookOpen, Coins, User, LogOut, GraduationCap, Users, Shield, School, LayoutDashboard, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import LanguageSwitcher from "./LanguageSwitcher";

const DashboardHeader = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, refreshUser } = useAuth();

  // Refresh user data on mount and when tab becomes visible
  useEffect(() => {
    // Refresh on mount
    refreshUser();
    
    // Refresh when tab becomes visible (user comes back to the tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUser();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success(t('success.saved'));
    navigate("/");
  };

  const getRoleIcon = () => {
    switch (user?.role) {
      case "teacher":
        return <GraduationCap className="w-4 h-4" />;
      case "parent":
        return <Users className="w-4 h-4" />;
      default:
        return <BookOpen className="w-4 h-4" />;
    }
  };

  // Ensure we have user data before rendering menu items
  const isAdmin = user?.is_admin === true;
  const isTeacher = user?.role === 'teacher';

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="container-main py-4">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#4F46E5] to-[#14B8A6] rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              BookTest<span className="text-[#4F46E5]">.pro</span>
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language Switcher */}
            <LanguageSwitcher variant="compact" />
            
            {/* Credits Display */}
            <Link to="/pricing">
              <Button variant="outline" className="gap-2" data-testid="header-credits-btn">
                <Coins className="w-4 h-4 text-[#F97316]" />
                <span className="font-semibold">{user?.credits ?? 0}</span>
                <span className="text-slate-500 hidden sm:inline">{t('common.credits')}</span>
              </Button>
            </Link>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="header-user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-600">
                      {user?.name?.charAt(0)?.toUpperCase() || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block">{user?.name || 'User'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
                <div className="px-2 py-2">
                  <p className="font-medium text-slate-900">{user?.name || 'User'}</p>
                  <p className="text-sm text-slate-500">{user?.email || ''}</p>
                  <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                    {getRoleIcon()}
                    <span className="capitalize">{user?.role || 'user'}</span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                
                {/* Dashboard - Always visible */}
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="nav-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  {t('nav.dashboard')}
                </DropdownMenuItem>
                
                {/* Profile */}
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="nav-profile">
                  <User className="w-4 h-4 mr-2" />
                  {t('nav.profile')}
                </DropdownMenuItem>
                
                {/* User Guide */}
                <DropdownMenuItem onClick={() => window.location.href = "/user-guide.html"} data-testid="nav-user-guide">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  User Guide
                </DropdownMenuItem>
                
                {/* Buy Credits */}
                <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="nav-pricing">
                  <Coins className="w-4 h-4 mr-2" />
                  {t('pricing.buyCredits')}
                </DropdownMenuItem>
                
                {/* Teacher Classrooms */}
                {isTeacher && (
                  <DropdownMenuItem onClick={() => navigate("/classrooms")} data-testid="nav-classrooms">
                    <School className="w-4 h-4 mr-2" />
                    {t('classrooms.title')}
                  </DropdownMenuItem>
                )}
                
                {/* Admin Dashboard */}
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin">
                      <Shield className="w-4 h-4 mr-2" />
                      {t('admin.title')}
                    </DropdownMenuItem>
                  </>
                )}
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="nav-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
